import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { signOut } from "firebase/auth";
import { auth } from "../../base";

import { AiOutlineLeft as BackIcon } from "react-icons/ai";

import { spellOutDate } from "../../utils/formatDate";
import { capitalize } from "../../utils/capitalize";
import {
  dateToInputValue,
  inputValueToDate,
  toTimestampLike,
} from "../../utils/adminLicenses";
import { grantTemporaryAccess, revokeMember } from "../../functions/accessApi";
import { TIERS, DEFAULT_TIER } from "../../data/tierAccess";
import TierPill from "./TierPill";
import StatusBadge from "./StatusBadge";

// Stripe statuses that keep an invite grant valid (mirror of the callable).
const GRACE_OK = new Set(["active", "trialing", "past_due"]);

// Default expiry for a temporary grant: one year out.
function oneYearOut() {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 1);
  return d;
}

// The renewal/expiry date a grant should show: invite grants surface the Stripe
// renewal (currentPeriodEnd), admin grants the explicit expiresAt.
function grantExpiry(grant) {
  const src = grant.source === "admin" ? grant.expiresAt : grant.currentPeriodEnd;
  return src?.seconds ? new Date(src.seconds * 1000) : null;
}

// Derive a display status: unclaimed, active (valid + claimed), or expired.
function grantStatus(grant) {
  let valid;
  if (grant.source === "admin") {
    const end = grantExpiry(grant);
    valid = !!end && end > new Date();
  } else {
    valid = GRACE_OK.has(grant.subscriptionStatus);
  }
  if (!valid) {
    return "expired";
  }
  return grant.memberUid ? "active" : "unclaimed";
}

// "Grant Temporary Access" form. Creates/overwrites a self-expiring admin grant
// keyed by email — the holder gets access until expiresAt with no Stripe
// subscription behind it.
function GrantAccessForm({ onGranted }) {
  const [email, setEmail] = useState("");
  const [tier, setTier] = useState(DEFAULT_TIER);
  const [expiry, setExpiry] = useState(dateToInputValue(oneYearOut()));
  const [busy, setBusy] = useState(false);

  const handleGrant = async (e) => {
    e.preventDefault();
    const target = email.trim();
    if (!target) {
      alert("Please enter an email address.");
      return;
    }
    const endDate = inputValueToDate(expiry);
    if (!endDate) {
      alert("Please choose an expiration date.");
      return;
    }
    setBusy(true);
    try {
      await grantTemporaryAccess(target, tier, endDate.getTime());
      onGranted({
        id: target.toLowerCase(),
        email: target,
        source: "admin",
        tier,
        expiresAt: toTimestampLike(endDate),
        memberUid: null,
        isOwner: false,
      });
      setEmail("");
    } catch (err) {
      console.error(err);
      alert("Something went wrong granting access. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="mintLicensesForm" onSubmit={handleGrant}>
      <p className="sectionHeading">Grant Temporary Access</p>
      <div className="mintFormRow">
        <div className="accountInputContainer">
          <label htmlFor="grantEmail">Email</label>
          <input
            id="grantEmail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{ width: "260px" }}
            placeholder="person@example.com"
          />
        </div>
        <div className="accountInputContainer">
          <label htmlFor="grantTier">Tier</label>
          <select
            id="grantTier"
            value={tier}
            onChange={(e) => setTier(e.target.value)}
          >
            {TIERS.map((t) => (
              <option key={t} value={t}>
                {capitalize(t)}
              </option>
            ))}
          </select>
        </div>
        <div className="accountInputContainer">
          <label htmlFor="grantExpiry">Expiration Date</label>
          <input
            id="grantExpiry"
            type="date"
            value={expiry}
            onChange={(e) => setExpiry(e.target.value)}
          />
        </div>
        <button type="submit" className="editButton" disabled={busy}>
          {busy ? "Granting…" : "Grant"}
        </button>
      </div>
    </form>
  );
}

// Searchable table of every access grant.
function GrantTable({ grants, busyEmail, onRevoke }) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return grants
      .filter((g) => {
        if (statusFilter !== "all" && grantStatus(g) !== statusFilter) {
          return false;
        }
        if (!q) {
          return true;
        }
        return (
          g.email?.toLowerCase().includes(q) ||
          g.providerEmail?.toLowerCase().includes(q)
        );
      })
      .sort((a, b) => a.email.localeCompare(b.email));
  }, [grants, search, statusFilter]);

  return (
    <div className="licensesContainer">
      <div className="licenseToolbar">
        <p className="sectionHeading">Access Grants</p>
        <div className="licenseToolbarControls">
          <input
            type="search"
            className="licenseSearch"
            placeholder="Search email or provider…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <select
            className="statusFilter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All statuses</option>
            <option value="unclaimed">Unclaimed</option>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
          </select>
          <span className="licenseCount">
            {visible.length} of {grants.length}
          </span>
        </div>
      </div>

      <div className="licenseTableScroll">
        <table className="licenseTable">
          <thead>
            <tr>
              <th>Email</th>
              <th>Source</th>
              <th>Tier</th>
              <th>Status</th>
              <th>Renews / Expires</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr>
                <td colSpan={6} className="emptyRow">
                  No grants match your filters.
                </td>
              </tr>
            ) : (
              visible.map((grant) => {
                const end = grantExpiry(grant);
                return (
                  <tr key={grant.id}>
                    <td className="ellipsisCell" title={grant.email}>
                      {grant.email}
                      {grant.isOwner ? " (owner)" : ""}
                    </td>
                    <td>{grant.source}</td>
                    <td>
                      <TierPill tier={grant.tier} />
                    </td>
                    <td>
                      <StatusBadge status={grantStatus(grant)} />
                    </td>
                    <td>{end ? spellOutDate(end) : "—"}</td>
                    <td>
                      <button
                        type="button"
                        className="rowButton rowButtonDanger"
                        disabled={grant.isOwner || busyEmail === grant.email}
                        title={
                          grant.isOwner
                            ? "Owner seats can't be revoked here."
                            : "Revoke this grant"
                        }
                        onClick={() => onRevoke(grant)}
                      >
                        Revoke
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminAccount(props) {
  const navigate = useNavigate();
  const { licenseData, setLicenseData } = props;
  const [busyEmail, setBusyEmail] = useState(null);

  // Newly-granted access appears at the top optimistically.
  const onGranted = (grant) =>
    setLicenseData((list) => [
      grant,
      ...list.filter((g) => g.id !== grant.id),
    ]);

  const onRevoke = async (grant) => {
    if (
      !window.confirm(
        `Revoke access for ${grant.email}? They lose access on next sign-in.`,
      )
    ) {
      return;
    }
    setBusyEmail(grant.email);
    try {
      await revokeMember(grant.email);
      setLicenseData((list) => list.filter((g) => g.id !== grant.id));
    } catch (err) {
      console.error(err);
      alert("Failed to revoke the grant. Please try again.");
    } finally {
      setBusyEmail(null);
    }
  };

  return (
    <>
      <div className="accountHeader">
        <h1 style={{ fontFamily: "NexaBold", fontSize: "22px" }}>My Account</h1>
        <div
          onClick={async () => {
            await signOut(auth);
            navigate("/login");
          }}
          id="backButtonContainer"
        >
          <BackIcon size={20} />
          <p style={{ paddingTop: "14px", paddingLeft: "5px" }}>Sign Out</p>
        </div>
      </div>

      <div className="licensesContainer">
        <GrantAccessForm onGranted={onGranted} />
      </div>

      <GrantTable
        grants={licenseData}
        busyEmail={busyEmail}
        onRevoke={onRevoke}
      />

      <div className="emailSubscriptionsContainer">
        <p className="sectionHeading">Email Subscriptions</p>
        <div className="emailSubscriptions">
          <p>Email</p>
          <p>Subscribed</p>
        </div>
        {props.userData.subscribers.map((subscriber) => (
          <div className="emailSubscriptions" key={subscriber.email}>
            <p>{subscriber.email}</p>
            <p>{subscriber.subscribed ? "Yes" : "No"}</p>
          </div>
        ))}
      </div>
    </>
  );
}
